import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, Loader } from 'lucide-react';

const Toast = ({ message, type = 'info', duration = 5000, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (type === 'loading') return;

    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onClose && onClose(), 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose, type]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle size={20} />;
      case 'error':
        return <XCircle size={20} />;
      case 'warning':
        return <AlertCircle size={20} />;
      case 'loading':
        return <Loader size={20} className="animate-spin" />;
      default:
        return <Info size={20} />;
    }
  };

  const getStyles = () => {
    const baseStyles = {
      position: 'fixed',
      top: '20px',
      right: '20px',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '16px 20px',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      fontFamily: 'var(--font-family)',
      fontSize: '14px',
      fontWeight: '500',
      maxWidth: '400px',
      transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
      opacity: isVisible ? 1 : 0,
      transition: 'all 0.3s ease-in-out',
      cursor: type === 'loading' ? 'default' : 'pointer'
    };

    switch (type) {
      case 'success':
        return {
          ...baseStyles,
          backgroundColor: '#f0f9ff',
          border: '1px solid #22c55e',
          color: '#15803d'
        };
      case 'error':
        return {
          ...baseStyles,
          backgroundColor: '#fef2f2',
          border: '1px solid #ef4444',
          color: '#dc2626'
        };
      case 'warning':
        return {
          ...baseStyles,
          backgroundColor: '#fffbeb',
          border: '1px solid #f59e0b',
          color: '#d97706'
        };
      case 'loading':
        return {
          ...baseStyles,
          backgroundColor: '#eff6ff',
          border: '1px solid #3b82f6',
          color: '#1d4ed8'
        };
      default:
        return {
          ...baseStyles,
          backgroundColor: '#f8fafc',
          border: '1px solid #64748b',
          color: '#475569'
        };
    }
  };

  if (!isVisible) return null;

  return (
    <div style={getStyles()} onClick={() => setIsVisible(false)}>
      {getIcon()}
      <span>{message}</span>
    </div>
  );
};

// Toast Container Component
export const ToastContainer = ({ toasts = [], removeToast }) => {
  return (
    <div style={{ position: 'fixed', top: 0, right: 0, zIndex: 9999 }}>
      {toasts.map((toast, index) => (
        <div key={toast.id} style={{ marginBottom: '10px' }}>
          <Toast
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={() => removeToast(toast.id)}
          />
        </div>
      ))}
    </div>
  );
};

// Hook for managing toasts
export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 5000) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type, duration }]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const success = useCallback(
    (message, duration) => addToast(message, 'success', duration),
    [addToast],
  );
  const error = useCallback(
    (message, duration) => addToast(message, 'error', duration),
    [addToast],
  );
  const warning = useCallback(
    (message, duration) => addToast(message, 'warning', duration),
    [addToast],
  );
  const info = useCallback(
    (message, duration) => addToast(message, 'info', duration),
    [addToast],
  );
  const loading = useCallback(
    (message) => addToast(message, 'loading', Infinity),
    [addToast],
  );

  return {
    toasts,
    addToast,
    removeToast,
    success,
    error,
    warning,
    info,
    loading
  };
};

export default Toast;
